/**
 * GTM Helper - Manages Google Tag Manager data layer pushes
 *
 * IMPORTANT: Clearing Mechanism
 * After pushing events to the dataLayer, we clear certain fields to prevent
 * stale data from affecting subsequent events. However, we must be careful
 * not to clear data too quickly, as third-party pixels (Meta, TikTok, etc.)
 * need time to read the data for conversion tracking.
 *
 * Protected events get a delayed clearing (2 seconds) to ensure pixels can read them.
 */
import { Gtm } from '.';
import { Event } from '../events';
import { Session } from '../types';
import * as Petsdeli from './petsdeli';
import { Queue } from './queue';

const dl =
  typeof window !== 'undefined'
    ? (window.dataLayer = window.dataLayer || [])
    : [];

// Critical events that should never be cleared immediately
// These events are typically used by pixels for conversion tracking
const PROTECTED_EVENTS = new Set([
  'Purchase', // Checkout purchase completion
  'Add To Cart', // Product added to cart
  'Submit Order', // Order submission
  'Submit Payment', // Payment submission
  'Ready Confirmation', // Checkout confirmation ready
  'Authentication', // User login/registration
]);

// Store timeout IDs to allow cleanup if needed
const clearingTimeouts: NodeJS.Timeout[] = [];

// Delay before clearing data (in milliseconds)
// This gives pixels and tags enough time to read the data
const CLEARING_DELAY_MS = 2000;

type Push = Event<unknown> | Gtm.Events | Gtm.State;

//Mediate between Events and Grouped Events
export interface NormalizedPush extends Partial<Session> {
  event: string;
  eventSource: 'petsdeli';
  eventName?: string;
  eventGroup?: string;
  eventData?: unknown;
  eventNonInteractive?: boolean;
}

function isGetSessionPush(x: NormalizedPush) {
  return 'clientIp' in x;
}

const queue = new Queue<NormalizedPush>();

let defer = true;

export async function gtm(...pushes: Push[]): Promise<void> {
  const normalized = pushes.reduce(normalizeEvents, []);

  if (defer) {
    const getSessionPush = normalized.find(isGetSessionPush);
    if (getSessionPush) {
      defer = false;
      queue.unshift(getSessionPush);
      normalized.splice(normalized.indexOf(getSessionPush), 1);
    } else {
      queue.push(...normalized);
      return queue.promise();
    }
  }
  queue.push(...normalized);

  return queue.run(
    (x, i, ls) =>
      Promise.all([
        Petsdeli.track(x),
        new Promise<void>((r) => {
          const timeOut = setTimeout(() => r(), 3000); //max timeout in case gtm doesn't respond on eventCallback

          dl.push({
            ...x,
            eventCallback: () => {
              clearTimeout(timeOut);
              r();
            },
          });
          if (i === ls.length - 1) {
            // Check if this is a protected event that shouldn't be cleared immediately
            const isProtectedEvent =
              x.eventName && PROTECTED_EVENTS.has(x.eventName);

            // Also check for events with important eventData (like checkout data)
            const hasImportantData =
              x.eventData &&
              typeof x.eventData === 'object' &&
              x.eventData !== null &&
              (Object.prototype.hasOwnProperty.call(x.eventData, 'checkout') ||
                Object.prototype.hasOwnProperty.call(x.eventData, 'product') ||
                Object.prototype.hasOwnProperty.call(x.eventData, 'items'));

            const shouldDelayClearing = isProtectedEvent || hasImportantData;

            if (shouldDelayClearing) {
              // For protected events, delay the clearing to give pixels time to read
              const timeoutId = setTimeout(() => {
                // Check if page is still active before clearing
                if (typeof window !== 'undefined' && !document.hidden) {
                  dl.push({
                    eventData: undefined,
                    eventName: undefined,
                    eventGroup: undefined,
                    eventSource: undefined,
                  });
                }
                // Remove timeout from tracking array
                const index = clearingTimeouts.indexOf(timeoutId);
                if (index > -1) {
                  clearingTimeouts.splice(index, 1);
                }
              }, CLEARING_DELAY_MS);

              // Track timeout for potential cleanup
              clearingTimeouts.push(timeoutId);
            } else {
              // For non-protected events, clear immediately as before
              dl.push({
                eventData: undefined,
                eventName: undefined,
                eventGroup: undefined,
                eventSource: undefined,
              });
            }
          }
        }),
      ]).then(() => {}) //function spec expects void to be returned
  );
}

function normalizeEvents(
  r: NormalizedPush[],
  x: Event<unknown> | Gtm.Events | Gtm.State
): NormalizedPush[] {
  const needsSessionEvent =
    'session' in x && (!('name' in x) || x.name !== 'Set Session');

  if (needsSessionEvent) {
    const { session } = x;
    r.push({
      event: 'Set Session',
      eventSource: 'petsdeli',
      ...session,
    });
  }

  if ('name' in x) {
    if ('group' in x) {
      r.push({
        event: `${x.group} ${x.name}`,
        eventSource: 'petsdeli',
        eventGroup: x.group,
        eventName: x.name,
        ...('data' in x ? { eventData: x.data } : {}),
        ...('nonInteractive' in x
          ? { eventNonInteractive: x.nonInteractive }
          : {}),
      });
    } else {
      r.push({
        event: x.name,
        eventSource: 'petsdeli',
        ...('properties' in x ? { eventData: x.properties } : {}),
      });
    }
  }

  return r;
}

/**
 * Cleanup function to clear pending timeouts if needed
 * (e.g., when navigating away from page)
 */
export function clearPendingTimeouts(): void {
  clearingTimeouts.forEach(clearTimeout);
  clearingTimeouts.length = 0;
}
